import {partition} from 'lodash';
import React, {FC, ReactNode, useMemo} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {TeammateModel} from '../../../../../../../libs/shared/core/src/models/api/teammateModel';
import {
  buildTeammateDisplayName,
  isTeammateActive,
} from '../../../../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {getFlatTeammates} from '../../../../../../../libs/shared/core/src/store/data/teammates/commonTeammatesSelectors';
import {TeammateAvatar} from '../../../../../../../libs/shared/ui/src/components/avatar/teammateAvatar';
import {boxedInputColorReaders} from '../../../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputConstants';
import {DropdownBoxCoordinator} from '../../../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {VisualSizesEnum} from '../../../../../../../libs/shared/ui/src/styles/commonStyles';
import {useAppSelector} from '../../../../../../helpers/appReduxHelpers';
import {MultiSelectSearchTeammateDropdown} from './multiSelectTeammateDropdown';

/*
 * Props.
 */

export interface MultiSelectTeammateFieldProps {
  initiallySelectedAliases: ReadonlySet<string>;
  onAliasesSelectionChange: (arg: ReadonlySet<string>) => void;
  dropdownTitle: ReactNode;
}

/*
 * Styles.
 */

const StyledPlaceholderSpan = styled.span`
  color: ${boxedInputColorReaders.textPlaceholder};
`;

const StyledButtonContentDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

/*
 * Components.
 */

export const MultiSelectTeammateField: FC<MultiSelectTeammateFieldProps> = (props) => {
  const {initiallySelectedAliases, onAliasesSelectionChange, dropdownTitle} = props;

  // Use blocked and visitor teammates in case the user explicitly wants to use one.
  const teammates = useAppSelector(getFlatTeammates);

  const [selectedTeammates, otherTeammates] = useMemo(
    () => partition(teammates, (teammate) => initiallySelectedAliases.has(teammate.alias)),
    [initiallySelectedAliases, teammates],
  );
  const sortedTeammates = useMemo(
    // Filter out blocked, bot and visitor teammates that have not been selected.
    () => [...selectedTeammates, ...otherTeammates.filter((t) => isTeammateActive(t))],
    [otherTeammates, selectedTeammates],
  );

  // We might want to remove aliases that do not correspond to any teammates.

  return (
    <DropdownBoxCoordinator
      leftIcon={selectedTeammates.length === 0 ? 'participantSmall' : undefined}
      value={renderInputTitle(selectedTeammates)}
      renderDropdown={(popoverProps) => (
        <MultiSelectSearchTeammateDropdown
          {...popoverProps}
          teammates={sortedTeammates}
          headerTitle={dropdownTitle}
          initiallySelectedAliases={new Set(initiallySelectedAliases)}
          onAliasesSelectionChange={onAliasesSelectionChange}
        />
      )}
      shouldDisablePadding
    />
  );
};

function renderInputTitle(selectedTeammates: ReadonlyArray<TeammateModel>) {
  const firstSelectedTeammate = selectedTeammates.at(0);
  if (!firstSelectedTeammate) {
    return (
      <StyledPlaceholderSpan>
        <Trans id="search-query-builder-teammate-placeholder">Select teammate</Trans>
      </StyledPlaceholderSpan>
    );
  }

  const teammateNameSuffix = selectedTeammates.length > 1 ? `, +${selectedTeammates.length - 1}` : '';

  const firstTeammate = (
    <StyledButtonContentDiv>
      <TeammateAvatar teammate={firstSelectedTeammate} size={VisualSizesEnum.SMALL} />
      {buildTeammateDisplayName(firstSelectedTeammate) + teammateNameSuffix}
    </StyledButtonContentDiv>
  );

  return firstTeammate;
}
